import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  Pressable,
  useWindowDimensions,
  Platform,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
} from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTranslation } from 'react-i18next';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import { AdminColors } from '../Admin/constants/adminColors';
import { styles as baseStyles } from './StopIssueSubmitScreen.styles';
import {
  fetchRoadblockScreenData,
  fetchRouteRoadblocks,
  submitRouteRoadblock,
  submitBulkRouteRoadblock,
  parseError,
  ScreenDataResponse,
  SubmissionReasonCode,
  SubmitBulkRouteRoadblockRequest,
  HarborImageUploadResult,
} from '../../api/deliveryRpmApi';
import { getLocalTimestamp } from '../../api/apiClient';
import HarborPhotoUpload from './components/HarborPhotoUpload';
import NotifySupervisorModal from './NotifySupervisorModal';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StopIssueSubmitScreenProps {
  onNavigate: (screen: string, params?: Record<string, any>) => void;
  route?: {
    params?: {
      sscc?: string;
      proceedWhenNotScheduled?: boolean;
      customer?: { cof: string; name: string; sscc?: string; ssccList?: string[] };
    };
  };
}

type SubmitRoadblockPayload = Parameters<typeof submitRouteRoadblock>[0] | SubmitBulkRouteRoadblockRequest;

// ─── Constants ────────────────────────────────────────────────────────────────

const DETAILS_MAX_LEN = 1000;
const TW_REASON_CODE = 'Time Window Adjustment (Permanent)';
const DOW_LABELS = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'] as const;
type DowLabel = typeof DOW_LABELS[number];

// Default labels when pictureTitle / textFieldTitle is blank
const DEFAULT_PHOTO_LABEL = 'Photo';
const DEFAULT_TEXT_LABEL = 'Additional Details';

// Generate half-hour HH:MM options 00:00 – 23:30
const TIME_OPTIONS: string[] = (() => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return opts;
})();

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDate = (iso: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};

/** Convert HH:MM string to total minutes since midnight. */
const toMinutes = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const UNICODE_EMOJI_REGEX = /\p{Extended_Pictographic}/u;

const hasUnicodeEmoji = (value: string) => UNICODE_EMOJI_REGEX.test(value);

// ─── Icons ────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <Svg width={12} height={12} viewBox="0 0 24 24" fill="none">
    <Path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ClockIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Circle cx={12} cy={12} r={9} stroke={AdminColors.blue700} strokeWidth={2} strokeLinecap="round" />
    <Path d="M12 7v5l3.5 2" stroke={AdminColors.blue700} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const WarnIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Path d="M12 3L2.5 20h19L12 3Z" stroke="#E5A408" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M12 9.5v4.5" stroke="#E5A408" strokeWidth={2} strokeLinecap="round" />
    <Circle cx={12} cy={17} r={0.4} fill="#E5A408" stroke="#E5A408" strokeWidth={2} />
  </Svg>
);

const ReasonCodeIcon: React.FC<{ iconName?: string }> = ({ iconName }) => {
  const styles = useDarkStyles(baseStyles);
  if (!iconName) {
    return <MaterialIcons name="help-outline" size={18} color={AdminColors.gray450} />;
  }
  if (hasUnicodeEmoji(iconName)) {
    return <Text style={styles.rcEmojiIcon}>{iconName}</Text>;
  }
  return <MaterialIcons name={iconName as any} size={18} color={AdminColors.blue700} />;
};

// ─── Simple time picker ───────────────────────────────────────────────────────
// A scrollable list of time options rendered as a modal-free inline picker.

interface TimePickerProps {
  id: 'open' | 'close';
  label: string;
  value: string;
  isOpen: boolean;
  onToggle: (id: 'open' | 'close') => void;
  onSelect: (val: string) => void;
}

const TimePicker: React.FC<TimePickerProps> = ({ id, label, value, isOpen, onToggle, onSelect }) => {
  const styles = useDarkStyles(baseStyles);
  return (
    <View style={[styles.timeField, isOpen && styles.timeFieldOpen]}>
      <Text style={styles.twLabel}>{label}</Text>
      <Pressable style={styles.timePickerBtn} onPress={() => onToggle(id)}>
        <Text style={[styles.timePickerText, !value && styles.timePickerPlaceholder]}>
          {value || 'HH:MM'}
        </Text>
        <MaterialIcons name={isOpen ? 'expand-less' : 'expand-more'} size={20} color={AdminColors.gray450} />
      </Pressable>
      {isOpen && (
        <View style={styles.timeDropdown}>
          <ScrollView nestedScrollEnabled style={{ maxHeight: 180 }}>
            {TIME_OPTIONS.map(opt => (
              <Pressable
                key={opt}
                style={[styles.timeOpt, value === opt && styles.timeOptSel]}
                onPress={() => { onSelect(opt); onToggle(id); }}
              >
                <Text style={[styles.timeOptText, value === opt && styles.timeOptTextSel]}>{opt}</Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

// ─── Main screen ──────────────────────────────────────────────────────────────

const StopIssueSubmitScreen: React.FC<StopIssueSubmitScreenProps> = ({ onNavigate, route }) => {
  const dc = useDarkColor();
  const styles = useDarkStyles(baseStyles);
  const { t, i18n } = useTranslation('harbor');
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const params = route?.params ?? {};
  const sscc = params.sscc ?? '';
  const proceedWhenNotScheduled = params.proceedWhenNotScheduled ?? false;
  const customerParam = params.customer;
  const displayCustomerSsccList = customerParam?.ssccList?.filter(Boolean) ?? (customerParam?.sscc ? [customerParam.sscc] : []);
  const customerPrimarySscc = displayCustomerSsccList[0] ?? customerParam?.sscc ?? '';
  const isBulkCustomerSubmission = displayCustomerSsccList.length > 1;
  const [customerSsccListModalVisible, setCustomerSsccListModalVisible] = useState(false);
  const { width: viewportWidth } = useWindowDimensions();
  const isMobile = viewportWidth < 768;

  const reasonTileColumns = useMemo(() => {
    if (viewportWidth < 768) return 2;
    if (viewportWidth < 1280) return 4;
    return 6;
  }, [viewportWidth]);

  const reasonTileWidth = useMemo(() => {
    const pageHorizontalPadding = 48;
    const cardHorizontalPadding = 48;
    const gridGap = 12;
    const availableWidth = Math.max(viewportWidth - pageHorizontalPadding - cardHorizontalPadding, 280);
    return Math.floor((availableWidth - gridGap * (reasonTileColumns - 1)) / reasonTileColumns);
  }, [viewportWidth, reasonTileColumns]);

  // ── Load screen data from API ────────────────────────────────────────────
  const [screenData, setScreenData] = useState<ScreenDataResponse | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {

      const effectiveSscc = sscc || customerPrimarySscc;
      const data = await fetchRoadblockScreenData(effectiveSscc!, proceedWhenNotScheduled, undefined, i18n.language);

      setScreenData(data);
    } catch (err) {
      setLoadError(parseError(err));
    } finally {
      setLoading(false);
    }
  }, [sscc, customerPrimarySscc, proceedWhenNotScheduled, i18n.language]);


  useEffect(() => {
    // Call screen-data whenever we have a real SSCC (direct entry or via customer).
    // The customer path now always arrives with a validated SSCC from the previous screen.
    if (sscc || customerPrimarySscc) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [sscc, customerPrimarySscc, loadData, i18n.language]);


  // Derive order info — prefer API response, fall back to customerParam
  const orderInfo = useMemo(() => {
    if (screenData?.orderInfo) {
      return {
        customer: screenData.orderInfo.customer,
        sscc: screenData.orderInfo.sscc,
        localRoute: screenData.orderInfo.localRoute,
        date: screenData.orderInfo.date,
      };
    }
    if (customerParam) {
      return {
        customer: customerParam.cof ? `${customerParam.cof} - ${customerParam.name}` : customerParam.name,
        sscc: displayCustomerSsccList[0] ?? customerParam.sscc ?? '—',
        localRoute: '—',
        date: todayIso(),
      };
    }
    return null;
  }, [screenData, customerParam, displayCustomerSsccList]);

  const reasonCodes: SubmissionReasonCode[] = screenData?.reasonCodes ?? [];

  // Store selected reason code ID (language-independent) to restore across language changes
  const savedReasonIdRef = useRef<number | null>(null);

  // ── Reason code ───────────────────────────────────────────────────────────
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [reasonError, setReasonError] = useState('');

  const selectedReason = useMemo(
    () => reasonCodes.find(r => r.id === selectedReasonId) ?? null,
    [reasonCodes, selectedReasonId],
  );

  // When a reason is selected, save its ID for restoration after language change
  useEffect(() => {
    if (selectedReasonId !== null) {
      savedReasonIdRef.current = selectedReasonId;
    }
  }, [selectedReasonId]);

  // When reasonCodes updates (e.g., from language change), restore previous selection by ID.
  // IDs stay constant across languages even though the displayed label is translated.
  useEffect(() => {
    if (savedReasonIdRef.current !== null && reasonCodes.length > 0) {
      const restored = reasonCodes.find(r => r.id === savedReasonIdRef.current);
      if (restored && restored.id !== selectedReasonId) {
        setSelectedReasonId(restored.id);
      }
    }
  }, [reasonCodes]);

  // Identify the "Time Window Adjustment (Permanent)" reason by its stable ID rather than its
  // translated label — `reasonCode` is localized per `i18n.language`, so a hardcoded English
  // string match would fail once the UI is switched to Spanish/French. We resolve the ID once via
  // an English-language lookup against the same config endpoint (id is language-independent).
  const [twReasonId, setTwReasonId] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchRouteRoadblocks({ roadblockTypes: 'Stop Issue', isActive: 'true', size: '100', language: 'en' })
      .then(res => {
        if (cancelled) return;
        const found = res.items.find(
          r => r.routeRoadblock?.trim().toLowerCase() === TW_REASON_CODE.toLowerCase(),
        );
        if (found) setTwReasonId(found.id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isTW = selectedReasonId !== null && selectedReasonId === twReasonId;

  // ── Derived display config from selected reason ───────────────────────────
  const showPhoto = !!(selectedReason?.pictureRequired || selectedReason?.pictureOptional);
  const photoRequired = !!selectedReason?.pictureRequired;
  const photoLabel = selectedReason?.pictureTitle?.trim() || t('stop_issue_submit.section_photo_attachment');

  const showTextField = !!(selectedReason?.textFieldRequired || selectedReason?.textFieldOptional);
  const textFieldRequired = !!selectedReason?.textFieldRequired;
  const textFieldLabel = selectedReason?.textFieldTitle?.trim() || t('stop_issue_submit.section_details');

  // ── Time Window state ─────────────────────────────────────────────────────
  // All days selected by default per spec
  const [twDays, setTwDays] = useState<Set<DowLabel>>(new Set(DOW_LABELS));
  const [twOpen, setTwOpen] = useState('');
  const [twClose, setTwClose] = useState('');
  const [openTimePicker, setOpenTimePicker] = useState<'open' | 'close' | null>(null);
  const [dowError, setDowError] = useState('');
  const [twGapError, setTwGapError] = useState('');

  const twGapMinutes = useMemo(() => {
    if (!twOpen || !twClose) return null;
    return toMinutes(twClose) - toMinutes(twOpen);
  }, [twOpen, twClose]);

  const twValid = useMemo(() => {
    if (!isTW) return true;
    if (twDays.size === 0) return false;
    if (!twOpen || !twClose) return false;
    if (twGapMinutes !== null && twGapMinutes < 240) return false;
    return true;
  }, [isTW, twDays, twOpen, twClose, twGapMinutes]);

  const resetTW = () => {
    setTwDays(new Set(DOW_LABELS));
    setTwOpen('');
    setTwClose('');
    setOpenTimePicker(null);
    setDowError('');
    setTwGapError('');
  };

  const handleToggleTimePicker = (id: 'open' | 'close') => {
    setOpenTimePicker(prev => (prev === id ? null : id));
  };

  const toggleDow = (day: DowLabel) => {
    setTwDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      setDowError(next.size === 0 ? t('stop_issue_submit.error_dow_required') : '');
      return next;
    });
  };

  const handleTwOpenChange = (val: string) => {
    setTwOpen(val);
    if (val && twClose) {
      const gap = toMinutes(twClose) - toMinutes(val);
      setTwGapError(gap < 240 ? t('stop_issue_submit.error_time_gap') : '');
    }
  };

  const handleTwCloseChange = (val: string) => {
    setTwClose(val);
    if (twOpen && val) {
      const gap = toMinutes(val) - toMinutes(twOpen);
      setTwGapError(gap < 240 ? t('stop_issue_submit.error_time_gap') : '');
    }
  };

  // ── Reason select ─────────────────────────────────────────────────────────
  const handleReasonSelect = (id: number) => {
    // Collapse and clear TW when switching away from TW reason
    if (isTW && id !== twReasonId) {
      resetTW();
    }
    setSelectedReasonId(id);
    setReasonError('');
    // Reset picture + details when reason changes
    setUploadResult(null);
    setPictureAttached(false);
    setDetails('');
  };

  // ── Picture attachment ────────────────────────────────────────────────────
  const [pictureAttached, setPictureAttached] = useState(false);
  const [uploadResult, setUploadResult] = useState<HarborImageUploadResult | null>(null);

  const handlePhotoUploaded = (result: HarborImageUploadResult) => {
    setUploadResult(result);
    setPictureAttached(true);
  };

  // ── Additional details ────────────────────────────────────────────────────
  const [details, setDetails] = useState('');

  // ── Submit state / toast ──────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);

  const [submitBannerError, setSubmitBannerError] = useState('');

  // ── Supervisor warning modal state ────────────────────────────────────────
  const [supervisorWarningVisible, setSupervisorWarningVisible] = useState(false);
  const [supervisorConfirmVisible, setSupervisorConfirmVisible] = useState(false);

  // ── Supervisor automatic notification modal state ─────────────────────────
  const [supervisorNotifyVisible, setSupervisorNotifyVisible] = useState(false);
  const [submittedSubmissionId, setSubmittedSubmissionId] = useState<number | null>(null);
  const [pendingSubmitPayload, setPendingSubmitPayload] = useState<SubmitRoadblockPayload | null>(null);

  // ── Dirty state for cancel guard ──────────────────────────────────────────
  const isDirty = selectedReasonId !== null || twDays.size !== DOW_LABELS.length || twOpen !== '' || twClose !== '' || pictureAttached || details.length > 0;
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);

  // ── Validation ────────────────────────────────────────────────────────────
  const pictureOk = !photoRequired || pictureAttached;
  const textFieldOk = !textFieldRequired || details.trim().length > 0;

  const isSubmitEnabled = useMemo(() => {
    if (!selectedReasonId) return false;
    if (!pictureOk) return false;
    if (!textFieldOk) return false;
    return twValid;
  }, [selectedReasonId, pictureOk, textFieldOk, twValid]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const buildPayload = (): SubmitRoadblockPayload => {
    const imageResponse = uploadResult
      ? JSON.stringify({ [uploadResult.originalFileName]: uploadResult.imageUrl })
      : undefined;
    const basePayload = {
      reasonCodeId: selectedReasonId!,
      daysOfWeek: isTW ? DOW_LABELS.filter(d => twDays.has(d)).join(',') : undefined,
      requestedOpenTime: isTW && twOpen ? `${twOpen}:00` : undefined,
      requestedCloseTime: isTW && twClose ? `${twClose}:00` : undefined,
      additionalDetails: details.trim() || undefined,
      imageResponse,
      overrideNotScheduled: proceedWhenNotScheduled || undefined,
      customerCofId: customerParam?.cof || undefined,
      location: selectedLocation?.name || '',
      clientTimestamp: getLocalTimestamp(),
      clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (isBulkCustomerSubmission) {
      return {
        ...basePayload,
        ssccList: displayCustomerSsccList,
      };
    }

    return {
      ...basePayload,
      sscc: sscc || orderInfo?.sscc || customerPrimarySscc || '',
    };
  };

  const performSubmit = async (payload: SubmitRoadblockPayload) => {
    setSubmitting(true);
    setSubmitBannerError('');
    try {
      const response = 'ssccList' in payload
        ? await submitBulkRouteRoadblock(payload)
        : await submitRouteRoadblock(payload);
      if (selectedReason?.supervisorAutomaticNotification) {
        setSubmittedSubmissionId(response.submissionId);
        setSupervisorNotifyVisible(true);
        return;
      }
      onNavigate('RouteRoadblocks', { toastMessage: t('stop_issue_submit.success_message') });
    } catch (err) {
      const msg = parseError(err);
      setSubmitBannerError(msg || 'Unable to submit roadblock. Please try again or contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    let valid = true;

    if (!selectedReasonId) {
      setReasonError(t('stop_issue_submit.error_reason_required'));
      valid = false;
    }

    if (isTW) {
      if (twDays.size === 0) {
        setDowError(t('stop_issue_submit.error_dow_required'));
        valid = false;
      }
      if (!twOpen || !twClose) {
        setTwGapError(t('stop_issue_submit.error_time_required'));
        valid = false;
      } else if (twGapMinutes !== null && twGapMinutes < 240) {
        setTwGapError(t('stop_issue_submit.error_time_gap'));
        valid = false;
      }
    }

    if (!valid) return;

    // Supervisor warning flow (step 1) takes precedence
    if (selectedReason?.supervisorNotificationWarning) {
      setSupervisorWarningVisible(true);
      return;
    }
    setSubmitting(true);
    setSubmitBannerError('');
    try {
      const imageResponse = uploadResult
        ? JSON.stringify({
            [uploadResult.originalFileName]: uploadResult.imageUrl,
          })
        : undefined;

      const response = await submitRouteRoadblock({
        sscc: sscc || orderInfo?.sscc || '',
        reasonCodeId: selectedReasonId!,
        daysOfWeek: isTW ? DOW_LABELS.filter(d => twDays.has(d)).join(',') : undefined,
        requestedOpenTime: isTW && twOpen ? `${twOpen}:00` : undefined,
        requestedCloseTime: isTW && twClose ? `${twClose}:00` : undefined,
        additionalDetails: details.trim() || undefined,
        imageResponse,
        overrideNotScheduled: proceedWhenNotScheduled || undefined,
        customerCofId: customerParam?.cof || undefined,
        location: selectedLocation?.name || '',
        language: i18n.language,
        clientTimestamp: getLocalTimestamp(),
        clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      // Check for automatic notification BEFORE navigating away
      if (selectedReason?.supervisorAutomaticNotification) {
        setSubmittedSubmissionId(response.submissionId);
        setSupervisorNotifyVisible(true);
      } else {
        // Navigate back; RouteRoadblocksScreen shows the success toast once mounted
        onNavigate('RouteRoadblocks', { toastMessage: t('stop_issue_submit.success_message') });
      }
    } catch (err) {
      const msg = parseError(err);
      setSubmitBannerError(msg || 'Unable to submit roadblock. Please try again or contact support.');
    } finally {
      setSubmitting(false);
    }

    const payload = buildPayload();

    // Supervisor automatic notification flow
    if (selectedReason?.supervisorAutomaticNotification) {
      setPendingSubmitPayload(payload);
      setSupervisorNotifyVisible(true);
      return;
    }

    await performSubmit(payload);
  };

  // ── Supervisor warning modal handlers ────────────────────────────────────
  const handleSupervisorWarningYes = () => {
    setSupervisorWarningVisible(false);
    performSubmit(buildPayload());
    const payload = buildPayload();
    if (selectedReason?.supervisorAutomaticNotification) {
      setPendingSubmitPayload(payload);
      setSupervisorNotifyVisible(true);
    } else {
      performSubmit(payload);
    }
  };

  const handleSupervisorWarningNo = () => {
    setSupervisorWarningVisible(false);
    setSupervisorConfirmVisible(true);
  };

  const handleSupervisorConfirmProceed = () => {
    setSupervisorConfirmVisible(false);
    performSubmit(buildPayload());
    const payload = buildPayload();
    if (selectedReason?.supervisorAutomaticNotification) {
      setPendingSubmitPayload(payload);
      setSupervisorNotifyVisible(true);
    } else {
      performSubmit(payload);
    }
  };

  const handleSupervisorConfirmBack = () => {
    setSupervisorConfirmVisible(false);
  };

  // The roadblock is already saved at this point, so closing or going back only leaves the screen.
  const handleSupervisorNotifyDone = () => {
    setSupervisorNotifyVisible(false);
    setSubmittedSubmissionId(null);
    onNavigate('RouteRoadblocks', { toastMessage: t('stop_issue_submit.success_message') });
  };

  const handleSupervisorNotifyClose = () => {
    setSupervisorNotifyVisible(false);
    if (pendingSubmitPayload) {
      performSubmit(pendingSubmitPayload);
      setPendingSubmitPayload(null);
    }
  };

  const handleSupervisorNotifyBack = () => {
    setSupervisorNotifyVisible(false);
    setPendingSubmitPayload(null);
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const handleCancel = () => {
    if (isDirty) {
      setCancelConfirmVisible(true);
    } else {
      onNavigate('RouteRoadblocks');
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <View style={styles.loadingWrap}>
        <ActivityIndicator size="large" color={AdminColors.blue700} />
      </View>
    );
  }

  if (loadError) {
    return (
      <View style={styles.errorBanner}>
        <MaterialIcons name="error-outline" size={20} color={AdminColors.red500} />
        <Text style={styles.errorBannerText}>{loadError}</Text>
        <TouchableOpacity onPress={() => onNavigate('RouteRoadblocks')}>
          <Text style={styles.returnLink}>Return</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!orderInfo) {
    return (
      <View style={styles.errorBanner}>
        <MaterialIcons name="error-outline" size={20} color={AdminColors.red500} />
        <Text style={styles.errorBannerText}>
          Order details unavailable for this SSCC. Please try again later or contact support.
        </Text>
        <TouchableOpacity onPress={() => onNavigate('RouteRoadblocks')}>
          <Text style={styles.returnLink}>Return</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.root} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      {/* ── Customer SSCC modal ─────────────────────────────────────────────── */}
      <Modal
        visible={customerSsccListModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setCustomerSsccListModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { maxWidth: 420, width: '100%' }]}>
            <Text style={styles.modalTitle}>{t('haulback_submit.customer_sscc_list_modal_title')}</Text>
            {displayCustomerSsccList.map((ssccValue, idx) => (
              <View
                key={`${ssccValue}-${idx}`}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingVertical: 6,
                  borderBottomWidth: idx < displayCustomerSsccList.length - 1 ? 1 : 0,
                  borderBottomColor: dc('#F0F0F0', 'border'),
                  width: '100%',
                }}
              >
                <Text style={{ fontSize: 13, color: dc('#838383'), fontFamily: 'System', minWidth: 20 }}>{idx + 1}.</Text>
                <Text style={{ fontSize: 14, color: dc('#1A1A1A'), fontFamily: 'System', letterSpacing: 0.5, flex: 1 }}>
                  {ssccValue}
                </Text>
              </View>
            ))}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalBtn}
                onPress={() => setCustomerSsccListModalVisible(false)}
                accessibilityLabel={t('stop_issue_submit.sscc_list_modal_ok')}
              >
                <Text style={styles.modalBtnText}>{t('stop_issue_submit.modal_action_stay')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Cancel confirmation dialog ────────────────────────────────────────── */}
      <Modal
        visible={cancelConfirmVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setCancelConfirmVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('stop_issue_submit.modal_unsaved_title')}</Text>
            <Text style={styles.modalBody}>
              {t('stop_issue_submit.modal_unsaved_body')}
            </Text>
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalSecondaryBtn}
                onPress={() => setCancelConfirmVisible(false)}
              >
                <Text style={styles.modalSecondaryBtnText}>{t('stop_issue_submit.modal_action_stay')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalBtn}
                onPress={() => {
                  setCancelConfirmVisible(false);
                  onNavigate('RouteRoadblocks');
                }}
              >
                <Text style={styles.modalBtnText}>{t('stop_issue_submit.modal_action_leave')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Supervisor warning modal (step 1): "Have you contacted your DDL?" ── */}
      <Modal
        visible={supervisorWarningVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setSupervisorWarningVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('stop_issue_submit.supervisor_warning_modal_title')}</Text>
            <Text style={styles.modalBody}>{t('stop_issue_submit.supervisor_warning_modal_body')}</Text>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalSecondaryBtn} onPress={handleSupervisorWarningNo}>
                <Text style={styles.modalSecondaryBtnText}>{t('stop_issue_submit.supervisor_warning_modal_no')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtn} onPress={handleSupervisorWarningYes}>
                <Text style={styles.modalBtnText}>{t('stop_issue_submit.supervisor_warning_modal_yes')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Supervisor warning modal (step 2): "Are you sure?" ── */}
      <Modal
        visible={supervisorConfirmVisible}
        transparent
        animationType="fade"
        onRequestClose={handleSupervisorConfirmBack}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('stop_issue_submit.supervisor_warning_confirm_title')}</Text>
            <Text style={styles.modalBody}>{t('stop_issue_submit.supervisor_warning_confirm_body')}</Text>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalSecondaryBtn} onPress={handleSupervisorConfirmBack}>
                <Text style={styles.modalSecondaryBtnText}>{t('stop_issue_submit.supervisor_warning_confirm_back')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtn} onPress={handleSupervisorConfirmProceed}>
                <Text style={styles.modalBtnText}>{t('stop_issue_submit.supervisor_warning_confirm_proceed')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Supervisor automatic notification modal ───────────────────────────── */}
      {supervisorNotifyVisible && submittedSubmissionId !== null && (
        <NotifySupervisorModal
          recordType="Roadblock"
          recordId={submittedSubmissionId}
          onClose={handleSupervisorNotifyDone}
          onBack={handleSupervisorNotifyDone}
        />
      )}

      <ScrollView
        style={styles.scrollRoot}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* ── Breadcrumb ─────────────────────────────────────────────────────── */}
        <View style={styles.breadcrumb}>
          <TouchableOpacity onPress={() => onNavigate('Harbor')}>
            <Text style={styles.bcLink}>{t('breadcrumb_harbor')}</Text>
          </TouchableOpacity>
          <Text style={styles.bcSep}>{' › '}</Text>
          <TouchableOpacity onPress={() => onNavigate('RouteRoadblocks')}>
            <Text style={styles.bcLink}>{t('route_roadblocks.page_title')}</Text>
          </TouchableOpacity>
          <Text style={styles.bcSep}>{' › '}</Text>
          <Text style={styles.bcCurrent}>{t('stop_issue_submit.page_title')}</Text>
        </View>

        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Text style={styles.pageTitle}>{t('stop_issue_submit.page_title')}</Text>
          <PTLanguageToggle compact={isMobile} />
        </View>

        {/* ── Order info box ────────────────────────────────────────────────── */}
        <View style={styles.infoBox}>
          <View style={styles.roLabel}>
            <Text style={styles.roText}>{t('stop_issue_submit.section_order_info_readonly')}</Text>
          </View>
          <View style={styles.infoGrid}>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('stop_issue_submit.label_sscc')}</Text>
              {displayCustomerSsccList.length > 1 ? (
                <TouchableOpacity
                  onPress={() => setCustomerSsccListModalVisible(true)}
                  accessibilityLabel={`${displayCustomerSsccList.length} SSCCs`}
                >
                  <Text style={[styles.infoVal, styles.mono, { color: dc('#034895'), textDecorationLine: 'underline' }]}>
                    {displayCustomerSsccList.length} SSCCs
                  </Text>
                </TouchableOpacity>
              ) : (
                <Text style={[styles.infoVal, styles.mono]}>{orderInfo.sscc}</Text>
              )}
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('stop_issue_submit.label_local_route')}</Text>
              <Text style={styles.infoVal}>{orderInfo.localRoute}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('stop_issue_submit.label_date')}</Text>
              <Text style={styles.infoVal}>{formatDate(orderInfo.date)}</Text>
            </View>
          </View>
        </View>

        {/* ── Reason Code card ─────────────────────────────────────────────── */}
        <View style={[styles.card, openTimePicker && styles.cardRaised]}>
          <Text style={styles.cardTitle}>{t('stop_issue_submit.section_reason')}</Text>
          <Text style={styles.cardSub}>
            {t('stop_issue_submit.section_reason_sub')}
          </Text>

          {reasonCodes.length === 0 ? (
            <Text style={styles.emptyText}>
              {t('common.no_data')}
            </Text>
          ) : (
            <View style={styles.rcGrid}>
              {reasonCodes.map(rc => {
                const isSel = selectedReasonId === rc.id;
                return (
                  <Pressable
                    key={rc.id}
                    style={[styles.rcTile, { width: reasonTileWidth }, isSel && styles.rcTileSel]}
                    onPress={() => handleReasonSelect(rc.id)}
                  >
                    {isSel && (
                      <View style={styles.rcCheck}>
                        <CheckIcon />
                      </View>
                    )}
                    <View style={styles.rcReasonRow}>
                      <View style={styles.rcReasonIconWrap}>
                        <ReasonCodeIcon iconName={rc.iconName} />
                      </View>
                      <Text style={styles.rcLabel}>{rc.reasonCode}</Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}

          {reasonError ? <Text style={styles.errorText}>{reasonError}</Text> : null}

          {/* ── Time Window sub-section ──────────────────────────────────── */}
          {isTW && (
            <View style={styles.twSection}>
              <View style={styles.twHeader}>
                <ClockIcon />
                <Text style={styles.twTitle}>{t('stop_issue_submit.section_time_window')}</Text>
              </View>
              <Text style={styles.twFine}>
                {t('stop_issue_submit.section_time_window_sub')}
              </Text>

              {/* Day-of-week toggles */}
              <View style={styles.dowRow}>
                {DOW_LABELS.map(day => {
                  const on = twDays.has(day);
                  return (
                    <Pressable
                      key={day}
                      style={[styles.dowBtn, on && styles.dowBtnOn]}
                      onPress={() => toggleDow(day)}
                    >
                      <Text style={[styles.dowText, on && styles.dowTextOn]}>{day}</Text>
                    </Pressable>
                  );
                })}
              </View>
              {dowError ? <Text style={styles.errorText}>{dowError}</Text> : null}

              {/* Open / Close time pickers */}
              <View style={styles.timeRow}>
                <TimePicker
                  id="open"
                  label={t('stop_issue_submit.label_open_time')}
                  value={twOpen}
                  isOpen={openTimePicker === 'open'}
                  onToggle={handleToggleTimePicker}
                  onSelect={handleTwOpenChange}
                />
                <TimePicker
                  id="close"
                  label={t('stop_issue_submit.label_close_time')}
                  value={twClose}
                  isOpen={openTimePicker === 'close'}
                  onToggle={handleToggleTimePicker}
                  onSelect={handleTwCloseChange}
                />
              </View>
              {twGapError ? <Text style={styles.errorText}>{twGapError}</Text> : null}
            </View>
          )}
        </View>

        {/* ── Photo Attachment — shown only when picture toggle is on ────────── */}
        {/* key forces remount on reason change so internal preview state clears */}
        {showPhoto && (
          <View key={`photo-${selectedReasonId ?? 'none'}`} style={styles.card}>
            <Text style={styles.cardTitle}>
              {photoLabel}
              {photoRequired ? <Text style={styles.requiredAsterisk}> *</Text> : null}
            </Text>
            <Text style={styles.cardSub}>
              {photoRequired
                ? t('stop_issue_submit.section_photo_attachment_required')
                : t('stop_issue_submit.section_photo_attachment_optional')}
            </Text>
            <HarborPhotoUpload
              label={pictureAttached ? t('stop_issue_submit.button_select_photo_attached') : t('stop_issue_submit.button_select_photo')}
              onUploaded={handlePhotoUploaded}
            />
          </View>
        )}

        {/* ── Additional Details card — shown only when text field toggle is on ── */}
        {showTextField && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>
              {textFieldLabel}
              {textFieldRequired && (
                <Text style={styles.requiredAsterisk}> *</Text>
              )}
            </Text>
            <Text style={styles.cardSub}>
              {textFieldRequired
                ? t('stop_issue_submit.section_details_required_sub')
                : t('stop_issue_submit.section_details_optional_sub')}
            </Text>
            <TextInput
              style={[
                styles.textArea,
                textFieldRequired && details.trim().length === 0 && styles.textAreaError,
              ]}
              multiline
              numberOfLines={4}
              value={details}
              onChangeText={v => setDetails(v.slice(0, DETAILS_MAX_LEN))}
              placeholder={
                textFieldRequired
                  ? t('stop_issue_submit.placeholder_details_required')
                  : t('stop_issue_submit.placeholder_details_optional')
              }
              placeholderTextColor={AdminColors.textMuted}
              textAlignVertical="top"
            />
            <Text style={[styles.charCounter, details.length === DETAILS_MAX_LEN && styles.charCounterWarn]}>
              {details.length}/{DETAILS_MAX_LEN}
            </Text>
            {textFieldRequired && details.trim().length === 0 && (
              <Text style={styles.errorText}>
                {t('stop_issue_submit.error_details_required')}
              </Text>
            )}
          </View>
        )}

        {!!submitBannerError && (
          <View style={styles.submitErrorBanner}>
            <WarnIcon />
            <Text style={styles.submitErrorBannerText}>{submitBannerError}</Text>
          </View>
        )}

        {/* ── Form actions ──────────────────────────────────────────────────── */}
        <View style={styles.formActions}>
          <TouchableOpacity style={styles.cancelBtn} onPress={handleCancel} disabled={submitting}>
            <Text style={styles.cancelBtnText}>{t('stop_issue_submit.button_cancel')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.submitBtn, (!isSubmitEnabled || submitting) && styles.submitBtnDisabled]}
            onPress={handleSubmit}
            disabled={!isSubmitEnabled || submitting}
          >
            {submitting
              ? <ActivityIndicator size="small" color="#fff" />
              : <Text style={[styles.submitBtnText, !isSubmitEnabled && styles.submitBtnTextDisabled]}>{t('stop_issue_submit.button_submit')}</Text>
            }
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

export default StopIssueSubmitScreen;
